// Web implementation of the popup component.

import { tokensToCssVars, useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import { HoverView } from 'component/basics';
import { translateLabel } from 'component/translation';
import { FocusLoopView } from 'platform-specific/focustrap';
import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import type { StyleProp, ViewStyle } from 'react-native';
import { Pressable, ScrollView, View } from 'react-native';
import { useStableCallback } from 'util/datastore';
import { t } from 'util/stdtypes';

type PopupBackdropProps = {
    onClick: () => void;
};
function PopupBackdrop({ onClick }: PopupBackdropProps) {
    return (
        <div style={{ position: 'fixed', left: 0, right: 0, top: 0, bottom: 0, zIndex: 900 }}>
            <Pressable
                accessibilityLabel={translateLabel({ label: t('popup.close_button', 'Close Popup') })}
                onPress={onClick}
                style={{ position: 'absolute', width: '100%', height: '100%', cursor: 'auto' }}
                testID='popup-backdrop'
            ></Pressable>
        </div>
    );
}

/*
WARN: those variables should be put into a context instead of declaring them
globally
*/
let global_active_popup_closer: (() => void) | null = null;
let global_clickTargetRef: React.RefObject<HTMLDivElement | null> | null = null;
let global_popupRef: React.RefObject<HTMLDivElement | null> | null = null;
let global_popup_align_right = false;
let global_popup_align_top = false;
let global_popup_matchWidth = false;
let global_popup_gap = 0;
let global_popup_center_on_trigger = false;

export function closeActivePopup() {
    if (global_active_popup_closer) {
        global_active_popup_closer();
    }
}

export function getPopupRef() {
    return global_popupRef;
}

// Portals to document.body — outside the ThemeProvider's ThemeScope div — so
// the theme's CSS custom properties don't inherit here, and CSS-module content
// (e.g. AccordionCard's var(--colorX) / var(--spacing-*)) would render
// unthemed. Mirror the active theme's vars onto the portal wrapper so var()
// resolves.
//
// Chosen over routing the portal into an in-scope container because:
// - it doesn't touch the FocusLoopView/focus-trap path, so it can't introduce
//   focus-loop bugs;
// - it's the smallest change; and
// - these modals are slated for a react-aria reimplementation anyway, which
//   will re-home their portals, so a structural fix now isn't worth it.
export function DocumentLevelComponent({ children }: { children: React.ReactNode }) {
    const theme = useTheme();
    return ReactDOM.createPortal(<div style={tokensToCssVars(theme)}>{children}</div>, document.body);
}

function global_layoutPopup() {
    if (!global_clickTargetRef || !global_popupRef) {
        return;
    }
    if (!global_popupRef.current || !global_clickTargetRef.current) {
        requestAnimationFrame(global_layoutPopup);
        return;
    }

    const node = global_popupRef.current;
    const rect = global_clickTargetRef.current.getBoundingClientRect();
    const nodeRect = node.getBoundingClientRect();
    const nodeWidth = nodeRect.width;
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;

    if (rect.top > windowHeight / 2) {
        if (global_popup_align_top) {
            node.style.top = '';
            node.style.bottom = windowHeight - rect.top + global_popup_gap + 'px';
            node.style.maxHeight = rect.top - global_popup_gap - 16 + 'px';
        } else {
            node.style.top = '';
            node.style.bottom = windowHeight - rect.top + 'px';
            node.style.maxHeight = rect.top - 16 + 'px';
        }
    } else if (global_popup_align_top) {
        node.style.top = '';
        node.style.bottom = windowHeight - rect.top + global_popup_gap + 'px';
        node.style.maxHeight = rect.top - global_popup_gap - 16 + 'px';
    } else {
        node.style.top = rect.bottom + global_popup_gap + 'px';
        node.style.bottom = '';
        node.style.maxHeight = windowHeight - rect.bottom - global_popup_gap - 16 + 'px';
    }
    if (global_popup_matchWidth) {
        node.style.left = rect.left + 'px';
        node.style.right = '';
        node.style.width = rect.width + 'px';
    } else if (global_popup_center_on_trigger) {
        const triggerCenter = rect.left + rect.width / 2;
        const popupLeft = triggerCenter - nodeWidth / 2;
        const clampedLeft = Math.max(16, Math.min(popupLeft, windowWidth - nodeWidth - 16));
        node.style.left = clampedLeft + 'px';
        node.style.right = '';
        node.style.maxWidth = windowWidth - 32 + 'px';
    } else if (rect.left > windowWidth / 2 || (global_popup_align_right && rect.right > nodeWidth)) {
        node.style.right = windowWidth - rect.right + 'px';
        node.style.left = '';
        node.style.maxWidth = rect.right - 16 + 'px';
    } else {
        node.style.left = rect.left + 'px';
        node.style.right = '';
        node.style.maxWidth = windowWidth - rect.left - 16 + 'px';
    }
    requestAnimationFrame(global_layoutPopup);
}

type UsePopupProps = {
    onToggle?: (isVisible: boolean) => void;
    enabled: boolean;
};

const usePopup = ({ enabled, onToggle }: UsePopupProps) => {
    const popupRef = React.useRef<HTMLDivElement>(null);
    const clickTargetRef = React.useRef<HTMLDivElement>(null);

    const [isVisible, setIsVisible] = useState(false);
    const stableOnToggle = useStableCallback(onToggle ?? (() => {}));

    const open = useCallback(() => {
        /*
        ensure that any visible popup is being closed before another one is
        opened
        */
        closeActivePopup();

        if (enabled) {
            setIsVisible(true);
            stableOnToggle(true);
        }
    }, [enabled, stableOnToggle]);

    const close = useCallback(() => {
        setIsVisible(false);
        stableOnToggle(false);
    }, [stableOnToggle]);

    useEffect(() => {
        if (isVisible) {
            global_active_popup_closer = close;
        } else if (global_active_popup_closer === close) {
            global_active_popup_closer = null;
        }
        return () => {
            if (global_active_popup_closer === close) {
                global_active_popup_closer = null;
            }
        };
    }, [isVisible, close]);

    return {
        popupRef,
        clickTargetRef,
        isVisible,
        close,
        open,
    };
};

export type PopupBaseProps = {
    /**
     * the content inside the popup
     */
    popupContent: (props: { onClose: () => void }) => React.JSX.Element;
    popupEnabled?: boolean;
    testID?: string;
    popupStyle?: StyleProp<ViewStyle> | null;
    matchWidth?: boolean;
    alignTop?: boolean;
    alignRight?: boolean;
    centerOnTrigger?: boolean;
    popupGap?: number;
    setHover?: (b: boolean) => void;
    setShown?: (b: boolean) => void;
    /**
     * The pressable element that triggers the popup
     */
    children: React.ReactNode;
};

export function PopupBase({
    popupContent,
    popupEnabled = true,
    testID,
    popupStyle = null,
    matchWidth = false,
    alignTop = false,
    alignRight = false,
    centerOnTrigger = false,
    popupGap = 0,
    setHover = (b: boolean) => {},
    setShown = (b: boolean) => {},
    children,
}: PopupBaseProps) {
    const s = usePopupButtonStyles();

    const { popupRef, clickTargetRef, close, open, isVisible } = usePopup({ onToggle: setShown, enabled: popupEnabled });

    useEffect(() => {
        if (isVisible) {
            global_clickTargetRef = clickTargetRef;
            global_popupRef = popupRef;
            global_popup_align_right = alignRight;
            global_popup_align_top = alignTop;
            global_popup_matchWidth = matchWidth;
            global_popup_gap = popupGap;
            global_popup_center_on_trigger = centerOnTrigger;
            global_layoutPopup();
        } else if (global_popupRef === popupRef) {
            global_clickTargetRef = null;
            global_popupRef = null;
        }
    }, [isVisible, alignRight, alignTop, centerOnTrigger, clickTargetRef, matchWidth, popupGap, popupRef]);

    return (
        <View style={s.frame}>
            <HoverView onPress={open} ref={clickTargetRef as any as React.RefObject<View>} setHover={setHover} testID={testID}>
                {children}
            </HoverView>
            {isVisible ? (
                <DocumentLevelComponent>
                    <PopupBackdrop onClick={close} />
                    <ScrollView
                        ref={popupRef as any as React.RefObject<ScrollView>}
                        style={[s.basicPopup, popupStyle]}
                        testID='popup-content'
                    >
                        <FocusLoopView index={2} onCloseLoop={close}>
                            {popupContent({ onClose: close })}
                        </FocusLoopView>
                    </ScrollView>
                </DocumentLevelComponent>
            ) : null}
        </View>
    );
}

// Popup content portals to document.body, outside the ThemeScope div, so CSS
// variables don't reach it — the theme must arrive via React context instead.
const usePopupButtonStyles = createThemedStyles((tokens) => ({
    frame: {
        position: 'relative',
    },
    basicPopup: {
        position: 'absolute',
        zIndex: 1000,
        backgroundColor: tokens.colors.colorSurfacePrimary,
        boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.10)' as any, // FIXME: boxShadow not typed for native
        borderRadius: 8,
        padding: 20,
    },
    up: {
        bottom: 0,
    },
    down: {
        top: 0,
    },
    left: {
        right: 0,
    },
    right: {
        left: 0,
    },
}));

export type PopupIconProps = { iconFamily: string; iconName: string };

export function PopupIcon({ iconFamily, iconName }: PopupIconProps) {
    return <View>{React.createElement(iconFamily, { name: iconName, size: 24, color: 'white' })}</View>;
}
